import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Region } from '@6grain/masofa-monolith-api-client/dictionaries';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';

interface RegionNode extends Region {
  children?: RegionNode[];
  isOpen?: boolean;
  isVisible?: boolean;
}

@Component({
  selector: 'app-region-tree-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ServerTranslatePipe],
  templateUrl: './region-tree-select.component.html',
  styleUrls: ['./region-tree-select.component.scss'],
})
export class RegionTreeSelectComponent implements OnChanges {
  @Input() regions: Region[] = [];
  @Input() selectedRegionId: string | null = null;
  @Input() maxLevel: number | null = null;
  @Output() regionSelected = new EventEmitter<string | null>();

  treeData: RegionNode[] = [];
  filteredTreeData: RegionNode[] = [];
  isOpen: boolean = false;
  filterText: string = '';
  selectedRegionName: string = '';

  private elementRef = inject(ElementRef);
  private translateService = inject(TranslateService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['regions'] && this.regions.length > 0) {
      this.buildTree();
      this.updateSelectedName();
    }
    if (changes['selectedRegionId']) {
      this.updateSelectedName();
    }
  }

  /**
   * Строит дерево регионов.
   * Проходит по всем регионам и вкладывает их в parents на основе parentId.
   */
  private buildTree(): void {
    const nodeMap = new Map<string, RegionNode>();

    this.regions.forEach((region) => {
      if (
        this.maxLevel !== null &&
        region.level != null &&
        region.level > this.maxLevel
      ) {
        return;
      }
      if (region.id) {
        nodeMap.set(region.id, {
          ...region,
          children: [],
          isOpen: false,
          isVisible: true,
        } as RegionNode);
      }
    });

    const roots: RegionNode[] = [];

    nodeMap.forEach((node) => {
      if (node.parentId === node.id) {
        roots.push(node);
        return;
      }
      if (node.parentId && nodeMap.has(node.parentId)) {
        const parent = nodeMap.get(node.parentId)!;
        parent.children!.push(node);
      } else {
        roots.push(node);
      }
    });
    this.sortTree(roots);
    this.treeData = roots;
    this.filterTree();
  }

  /**
   * Рекурсивная сортировка узлов по алфавиту с учетом текущего языка
   */
  private sortTree(nodes: RegionNode[]): void {
    const currentLang = this.translateService.currentLang || 'ru-RU';

    nodes.sort((a, b) => {
      const nameA = this.getNodeName(a, currentLang);
      const nameB = this.getNodeName(b, currentLang);
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        this.sortTree(node.children);
      }
    });
  }

  /**
   * Получение имени для сортировки с фоллбеком (если перевода нет)
   */
  private getNodeName(node: RegionNode, lang: string): string {
    if (!node.names) return '';

    const names = node.names as Record<string, string | undefined>;

    return (
      names[lang] ||
      names['ru-RU'] ||
      names['en-US'] ||
      Object.values(names)[0] ||
      ''
    );
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.filterText = '';
      this.filterTree();
    }
  }

  toggleParent(parent: RegionNode, event: Event): void {
    event.stopPropagation();
    parent.isOpen = !parent.isOpen;
  }

  selectRegion(region: Region): void {
    this.selectedRegionId = region.id!;
    this.regionSelected.emit(region.id);
    this.updateSelectedName();
    this.isOpen = false;
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    this.selectedRegionId = null;
    this.regionSelected.emit(null);
    this.selectedRegionName = '';
  }

  onFilterChange(): void {
    this.filterTree();
  }

  private filterTree(): void {
    if (!this.filterText) {
      this.filteredTreeData = this.treeData.map((p) => ({
        ...p,
        isVisible: true,
      }));
      return;
    }

    const lowerFilter = this.filterText.toLowerCase();

    this.filteredTreeData = this.treeData
      .map((parent) => {
        const parentMatches = this.checkNameMatch(parent, lowerFilter);
        const matchingChildren =
          parent.children?.filter((child) =>
            this.checkNameMatch(child, lowerFilter)
          ) || [];
        if (parentMatches || matchingChildren.length > 0) {
          return {
            ...parent,
            children: matchingChildren,
            isOpen: true,
            isVisible: true,
          };
        }
        return { ...parent, isVisible: false };
      })
      .filter((p) => p.isVisible);
  }

  private checkNameMatch(region: Region, filter: string): boolean {
    if (!region.names) return false;
    return Object.values(region.names).some(
      (name) => name && name.toLowerCase().includes(filter)
    );
  }

  private updateSelectedName(): void {
    if (!this.selectedRegionId) {
      this.selectedRegionName = '';
      return;
    }
    const region = this.regions.find((r) => r.id === this.selectedRegionId);
  }

  getRegionById(id: string | null): Region | undefined {
    return this.regions.find((r) => r.id === id);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
}
